import { useState, useMemo, useCallback, useEffect, useRef } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { type Column, type ColumnDef } from '@tanstack/react-table'
import { toast } from 'sonner'
import * as XLSX from 'xlsx'
import { Badge } from '@/components/ui/badge'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { ExtractionTable } from '@/components/extraction/ExtractionTable'
import { CitationLink } from '@/components/extraction/CitationLink'
import { COMPLIANCE_STATUS_OPTIONS } from '@/components/extraction/StatusDropdown'
import { ColumnsPicker, type ColumnOption } from '@/components/extraction/ColumnsPicker'
import { GroupBySelect, type GroupByOption } from '@/components/extraction/GroupBySelect'
import { ProgressCell } from '@/components/extraction/ProgressCell'
import { OwnerPicker } from '@/components/extraction/OwnerPicker'
import { RowActionsMenu } from '@/components/extraction/RowActionsMenu'
import { AddItemModal, type ModalFieldConfig } from '@/components/extraction/AddItemModal'
import { BulkActionsBar } from '@/components/ui/bulk-actions-bar'
import { BulkConfirmDelete } from '@/components/extraction/BulkConfirmDelete'
import { BulkValuePicker } from '@/components/ui/bulk-value-picker'
import { BulkOwnerPicker } from '@/components/extraction/BulkOwnerPicker'
import { useTableFilterBar, type ExtraFilterChip } from '@/components/extraction/TableFilterBar'
import { useTableFilters } from '@/hooks/use-table-filters'
import { addItemWithFeedback } from '@/lib/add-item-feedback'
import { buildExportSheet, exportScopeNote } from '@/lib/export-provenance'
import {
  buildOwnerOptions,
  parseFilterList,
  UNASSIGNED_OWNER,
  type FilterGroups,
} from '@/lib/table-filters'
import {
  PositionPill,
  POSITION_OPTIONS,
  displayPositionLabel,
} from '@/components/extraction/PositionPill'
import { splitRationaleAction } from '@/components/extraction/AssessmentBlock'
import { RequirementDrawer } from '@/components/extraction/RequirementDrawer'
import { formatReqRef, parseReqRef } from '@/lib/requirement-refs'
import { GradientSparkleIcon } from '@/components/extraction/GradientSparkleIcon'
import { Button } from '@/components/ui/button'
import { Trash2, ChevronDown, ChevronRight, Check, Loader2, MessageSquare, Sparkles } from 'lucide-react'
import { useExtractionStore } from '@/stores/extraction-store'
import { useCommentsStore } from '@/stores/comments-store'
import { useCoverageStore } from '@/stores/coverage-store'
import { useSectionFlagsStore } from '@/stores/section-flags-store'
import { ExpectationPill } from '@/components/requirement-coverage/ExpectationPill'
import { CoverageSummaryStrip } from '@/components/requirement-coverage/CoverageSummaryStrip'
import {
  AT_RISK_STATES,
  MANDATORY_AT_RISK_STATES,
  displayStateOf,
  isAtRisk,
  submissionRelevant,
  countMandatoryRisk,
} from '@/lib/compliance-position'
import {
  effectiveProgress,
  PROGRESS_FILTER_OPTIONS,
  PROGRESS_LABELS,
} from '@/lib/compliance-progress'
import {
  readTableView,
  tableViewStorageKey,
  writeTableView,
  type TableViewState,
} from '@/lib/table-view'
import { useAuthStore } from '@/stores/auth-store'
import {
  expectationConfig,
  EXPECTATION_ORDER,
  coverageStatusConfig,
  FILTERABLE_COVERAGE_STATUSES,
  type FilterableCoverageStatus,
} from '@/components/requirement-coverage/coverage-status'
import {
  COMPLIANCE_CATEGORIES,
  COMPLIANCE_CATEGORY_COLORS,
  type ExtractedComplianceItem,
  type ComplianceCategory,
  type CriticalityValue,
  type PositionValue,
  type ResponseExpectation,
} from '@/types/extraction'
import { api, type CoverageStatus } from '@/lib/api'
import { cn } from '@/lib/utils'
import { formatSourceWithPage } from '@/lib/citation-preview'

// ===================
// Types
// ===================

interface ComplianceMatrixExtractionProps {
  bidId: string
  readOnly?: boolean
}

interface TeamMember {
  id: string
  userId: string
  fullName: string | null
  avatarUrl: string | null
}

// ===================
// Constants
// ===================

const ALL_CATEGORIES: readonly ComplianceCategory[] = COMPLIANCE_CATEGORIES

const CRITICALITY_OPTIONS: Array<{ value: CriticalityValue; label: string }> = [
  { value: 'mandatory', label: 'Mandatory' },
  { value: 'desirable', label: 'Desirable' },
  { value: 'information', label: 'Information' },
]

/**
 * The row diet (spec B2). Eleven columns made a requirement unreadable, so the
 * table defaults to the five a bid manager acts on — Requirement, Can we
 * comply?, Where answered, Owner, Discussion — and everything else is opt-in
 * through the Columns picker.
 *
 * This list is the single source of truth for the picker: a column that is
 * toggleable but not listed here would be unreachable, and one listed but
 * absent from the table would be a dead tick.
 */
const HIDEABLE_COLUMNS: ColumnOption[] = [
  // `req` replaced the positional `#` column (id 'number'). A new id, not a
  // relabel: saved views list 'number' as hidden (it was hidden by default),
  // and reusing it would bury REQ for everyone who ever opened the matrix. A
  // stale 'number' entry in a saved view now names no column and is ignored.
  { id: 'req', label: 'REQ' },
  { id: 'category', label: 'Category' },
  { id: 'refClause', label: 'Ref/Clause' },
  { id: 'expectation', label: 'Response type' },
  { id: 'source', label: 'Source' },
  { id: 'progress', label: 'Progress' },
  { id: 'discussion', label: 'Discussion' },
  { id: 'actions', label: 'Row actions' },
]

/**
 * Hideable and part of the default five — the one column that is both, so the
 * default hidden set can no longer be the whole picker list. Deriving it that
 * way would bury the column the moment it became switchable.
 */
const DEFAULT_VISIBLE_HIDEABLE = new Set(['req', 'discussion'])

const DEFAULT_HIDDEN = HIDEABLE_COLUMNS.map((c) => c.id).filter(
  (id) => !DEFAULT_VISIBLE_HIDEABLE.has(id)
)

const DEFAULT_VIEW: TableViewState = { hiddenColumns: DEFAULT_HIDDEN, groupBy: null }

const GROUP_OPTIONS: GroupByOption[] = [
  { value: 'category', label: 'Category' },
  { value: 'owner', label: 'Owner' },
  { value: 'position', label: 'Can we comply?' },
  { value: 'expectation', label: 'Response type' },
]

/** Rows with no owner sort last, under a name no team member can collide with. */
const UNASSIGNED_GROUP = 'Unassigned'

/**
 * Owned, but by someone this bid cannot name — either a member whose `fullName`
 * is null, or an owner id no longer on the bid. One bucket, named once: it is
 * both a value the accessor produces and the last-but-one entry in the group
 * order, so it must never be listed twice.
 */
const UNKNOWN_OWNER_GROUP = 'Unknown'

/**
 * The export is ONE internal working document — there is deliberately no
 * client-facing variant (a returnable mode was cut: at current extraction
 * recall the AI-built table will not line up with a client's own schedule
 * rows). It leaves the app as a spreadsheet, so the sheet has to label itself.
 */
const EXPORT_INTERNAL_NOTE =
  'Internal working matrix — AI-assessed, verify before sharing externally'

/**
 * Qualifier stamped onto the "Recommended action" cell of a manually-set row.
 *
 * The action comes out of the AI's rationale, so on a row whose position the
 * team has since set by hand it is a superseded suggestion, not an open task —
 * the same misread fixed in `AssessmentBlock`. The sheet cannot borrow that
 * fix: it has no muted-text tier and no adjacent "Last AI assessment" heading,
 * and its column header is shared by every row, so the qualifier has to live in
 * the per-row cell. Prefixing rather than blanking keeps the AI's suggested
 * step in the export (blank would also read as "the AI suggested nothing") and
 * keeps it attached to its own row when the sheet is sorted or filtered.
 */
const MANUAL_ACTION_PREFIX = 'AI’s recommendation (position set manually): '

/**
 * Accepted `?position=` deep-link values. Three of them are display states, not
 * stored enum values: `unassessed` splits into "Needs review" (assessed and the
 * assessor abstained), "Needs your confirmation" (assessed, but never searched
 * for by design — mig 180) and "Not assessed" (never looked at), and the
 * filters must preserve that distinction.
 */
const POSITION_PARAMS = [
  'comply',
  'partial',
  'non_comply',
  'not_applicable',
  'unassessed',
  'needs_review',
  'needs_confirmation',
  'not_assessed',
] as const
type PositionParam = (typeof POSITION_PARAMS)[number]

/**
 * `at_risk` is a pseudo-value on top of the display states: "at risk" is
 * non_comply OR either assessed-unresolved state (`AT_RISK_STATES`), which no
 * single stored value expresses. On a mandatory-only link it also takes
 * "Not assessed" (`MANDATORY_AT_RISK_STATES`) — a mandatory row nobody has
 * vouched for is at risk, and the "N mandatory at risk" count includes it. Multi-select positions can now say it
 * outright, so the param expands into those values rather than driving a
 * hidden boolean. Deliberately
 * NOT offered in the filter dropdown — the chip is the in-page affordance; the
 * param exists for links arriving from the Go/No-Go banner, the Overview tile
 * and next-best-action nudges.
 */
const AT_RISK_PARAM = 'at_risk'

const POSITION_FILTER_OPTIONS: Array<{ value: PositionParam; label: string }> = [
  { value: 'comply', label: 'Comply' },
  { value: 'partial', label: 'Partial' },
  { value: 'non_comply', label: 'Non-comply' },
  { value: 'not_applicable', label: 'N/A' },
  { value: 'needs_review', label: 'Needs review' },
  { value: 'needs_confirmation', label: 'Needs your confirmation' },
  { value: 'not_assessed', label: 'Not assessed' },
  { value: 'unassessed', label: 'Unassessed (any)' },
]

/**
 * Every value the `position` matcher can be handed — from the dropdown, from a
 * deep link, or restored from storage — is offered as an option above, so
 * `pruneValues` never silently drops a live position selection and the group
 * needs no `skipPrune` escape hatch. Every `MANDATORY_AT_RISK_STATES` value is in the list.
 */
const POSITION_VALUES = new Set<string>(POSITION_PARAMS)

/** Derived, never hand-listed: a new criticality must not become an unlinkable value. */
const CRITICALITY_VALUES = new Set<string>(CRITICALITY_OPTIONS.map((o) => o.value))

/**
 * The REQ column's header: the one sortable column in this table, so it carries
 * its own compact variant of `BidsTable`'s SortableHeader rather than a shared one.
 * Click cycles ascending → descending → original order.
 */
function ReqSortHeader({ column }: { column: Column<ExtractedComplianceItem, unknown> }) {
  const sorted = column.getIsSorted()
  return (
    <Button
      variant="ghost"
      onClick={() => column.toggleSorting()}
      className={cn(
        'group -ml-2 h-6 px-2 text-xs hover:bg-transparent hover:text-foreground',
        sorted ? 'font-medium text-foreground' : 'font-medium text-muted-foreground'
      )}
    >
      REQ
      <ChevronDown
        aria-hidden="true"
        className={cn(
          'ml-1 h-3.5 w-3.5 shrink-0 transition-[opacity,transform] duration-150',
          sorted === 'asc' && 'rotate-180',
          sorted
            ? 'text-muted-foreground opacity-100'
            : 'text-subtle opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100'
        )}
      />
    </Button>
  )
}

function matchesPositionFilter(item: ExtractedComplianceItem, filter: PositionParam): boolean {
  if (filter === 'unassessed') return (item.position ?? 'unassessed') === 'unassessed'
  return displayStateOf(item) === filter
}

/**
 * Map the next-best-action deep-link params onto filter values.
 *
 * Note the asymmetry, which is deliberate and load-bearing: `?position=at_risk`
 * means "at risk regardless of criticality", whereas the "N mandatory at risk"
 * chip means "mandatory AND at risk". They are different views and must stay so.
 * Returns null when the URL carries no filter intent, so stored state is used.
 */
/**
 * Every one-shot view param this table lands on, spent once applied.
 *
 * `position` / `criticality` / `source` are read by `complianceUrlSeed`.
 * `coverage` is NOT — the component reads it straight into its own state — but
 * it is the same kind of intent and had the same defect: read on every mount and
 * never consumed, so dismissing its chip worked until a sub-tab round trip
 * remounted the matrix and the URL put it back. Spending it here is what makes
 * it dismissable; `useTableFilters` consumes the listed params whether or not
 * its own seed produced anything from them.
 *
 * Module-level so the reference is stable — it sits in an effect dep array.
 */
const COMPLIANCE_FILTER_PARAMS = ['position', 'criticality', 'source', 'coverage'] as const

export function complianceUrlSeed(params: URLSearchParams): Record<string, string[]> | null {
  const patch: Record<string, string[]> = {}

  const criticalities = parseFilterList(params.get('criticality'), CRITICALITY_VALUES)

  const rawPosition = params.get('position')
  if (rawPosition === AT_RISK_PARAM) {
    // A filter can't say "mandatory not-assessed OR any non-comply", so the
    // mandatory-only states apply only when the link is mandatory-only.
    const mandatoryOnly = criticalities.size === 1 && criticalities.has('mandatory')
    patch['position'] = [...(mandatoryOnly ? MANDATORY_AT_RISK_STATES : AT_RISK_STATES)]
  } else {
    const positions = parseFilterList(rawPosition, POSITION_VALUES)
    if (positions.size > 0) patch['position'] = [...positions]
  }

  if (criticalities.size > 0) patch['criticality'] = [...criticalities]

  if (params.get('source') === 'ai') patch['source'] = ['ai']

  return Object.keys(patch).length > 0 ? patch : null
}

/**
 * @param coverageStatusOf The row's derived response-coverage status, or
 *   `undefined` when it has none. Progress is a DERIVED value — for a narrative
 *   row the coverage loop owns it — so the builder cannot read it off the item
 *   and has to be handed the lookup. Filtering on the stored column instead is
 *   what made "In review" unaskable: no stored status carries it.
 */
export function buildComplianceFilterGroups(
  items: ExtractedComplianceItem[],
  teamMembers: Array<{ userId: string; fullName: string | null }>,
  coverageStatusOf: (item: ExtractedComplianceItem) => CoverageStatus | undefined
): FilterGroups<ExtractedComplianceItem> {
  return {
    category: {
      label: 'Category',
      options: ALL_CATEGORIES.map((c) => ({ value: c, label: c })),
      accessor: (item) => item.category,
    },
    progress: {
      label: 'Progress',
      options: PROGRESS_FILTER_OPTIONS.map((o) => ({ value: o.value, label: o.label })),
      // A matcher, not an accessor: post-award rows have no bucket at all, and
      // a derived row's bucket lives in the coverage store, not on the item.
      matcher: (item, value) => effectiveProgress(item, coverageStatusOf(item)).bucket === value,
    },
    position: {
      // Not a plain equality: three options are display states, and
      // `unassessed` deliberately spans all three.
      label: 'Position',
      options: POSITION_FILTER_OPTIONS.map((o) => ({ value: o.value, label: o.label })),
      matcher: (item, value) => matchesPositionFilter(item, value as PositionParam),
    },
    criticality: {
      label: 'Criticality',
      options: CRITICALITY_OPTIONS.map((o) => ({ value: o.value, label: o.label })),
      accessor: (item) => item.criticality,
    },
    owner: {
      label: 'Owner',
      options: buildOwnerOptions(items, teamMembers),
      // Coalesce, or the "Unassigned" option would match no row at all.
      accessor: (item) => item.ownerId ?? UNASSIGNED_OWNER,
      hideWhenSparse: true,
      // Team members arrive asynchronously; without this a restored owner
      // selection would be pruned on first render, before they land.
      skipPrune: true,
      // The selection outlives both sources of options, so it can end up
      // nameable by neither. Without this the chip printed the raw UUID.
      missingLabel: 'Not on this bid',
    },
    source: {
      /**
       * A matcher, not an accessor on `positionSource` — that column does not
       * mean what the labels say.
       *
       * `position_source` is a write-time OWNERSHIP guard: `NOT NULL DEFAULT
       * 'ai'`, and the assessor's UPDATE carries `WHERE position_source = 'ai'`
       * so a user override fences the AI out of that row. Every row the AI has
       * never touched is therefore already 'ai'. Reading it as provenance made
       * "AI-assessed" return the whole table on a bid with no assessment run,
       * and "Manually set" return nothing.
       *
       * `positionAssessedAt` is the honest signal — only the assessment sets
       * it, and clearing a position nulls it again. So AI-assessed means
       * AI-owned AND actually stamped; Manually set stands on ownership alone,
       * because a person can take a position with no assessment behind it.
       *
       * A row nobody has assessed matches neither option, deliberately. Those
       * are found under Position → "Not assessed", which is what that option is
       * for.
       */
      label: 'Source',
      options: [
        { value: 'ai', label: 'AI-assessed' },
        { value: 'manual', label: 'Manually set' },
      ],
      matcher: (item, value) =>
        value === 'manual'
          ? item.positionSource === 'manual'
          : item.positionSource !== 'manual' && item.positionAssessedAt != null,
    },
  }
}

// ===================
// Component
// ===================

export function ComplianceMatrixExtraction({ bidId, readOnly }: ComplianceMatrixExtractionProps) {
  const [showAddRow, setShowAddRow] = useState(false)
  // ?coverage=unmapped deep links (next-best-action rule 6) pre-engage the filter strip
  const [searchParams, setSearchParams] = useSearchParams()
  const [coverageFilter, setCoverageFilter] = useState<FilterableCoverageStatus | null>(() => {
    const param = searchParams.get('coverage')
    // Validated against what the strip can DRAW, not against `CoverageStatus`:
    // seeding a status with no button (`mapped`) would narrow the table with no
    // affordance to switch it off. Nothing in-app produces `?coverage=` today,
    // so this only closes a hand-typed URL — but it closes it at the source.
    return FILTERABLE_COVERAGE_STATUSES.find((status) => status === param) ?? null
  })
  const [showPostAward, setShowPostAward] = useState(false)
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([])
  /** The row whose details the drawer is showing — the id, not the row object. */
  const [activeItemId, setActiveItemId] = useState<string | null>(null)

  // Per-user, per-bid view state: which columns are hidden and what the rows
  // are grouped by. Same scoping as the filters — a shared browser must not
  // hand the next account the previous one's column diet.
  const userId = useAuthStore((s) => s.user?.id)
  const viewKey = tableViewStorageKey(userId, bidId, 'compliance_matrix')
  const [viewState, setViewState] = useState<TableViewState>(
    () => readTableView(viewKey) ?? DEFAULT_VIEW
  )

  // Switching bids must reset the view, and React Router reuses this element
  // across `:bidId` changes. Adjusted during render rather than in an effect for
  // the reason `use-table-filters.ts` documents at length: an effect would let
  // bid A's view reach the persistence effect under bid B's key, overwriting it.
  // Held in state, not a ref, so a discarded transition render discards it too.
  const [prevViewKey, setPrevViewKey] = useState(viewKey)
  if (prevViewKey !== viewKey) {
    setPrevViewKey(viewKey)
    setViewState(readTableView(viewKey) ?? DEFAULT_VIEW)
  }

  useEffect(() => {
    writeTableView(viewKey, viewState)
  }, [viewKey, viewState])

  const columnVisibility = useMemo(
    () => Object.fromEntries(viewState.hiddenColumns.map((id) => [id, false])),
    [viewState.hiddenColumns]
  )

  const toggleColumn = useCallback((id: string) => {
    setViewState((prev) => ({
      ...prev,
      hiddenColumns: prev.hiddenColumns.includes(id)
        ? prev.hiddenColumns.filter((c) => c !== id)
        : [...prev.hiddenColumns, id],
    }))
  }, [])

  const setGroupBy = useCallback((value: string | null) => {
    setViewState((prev) => ({ ...prev, groupBy: value }))
  }, [])

  const hiddenColumnSet = useMemo(
    () => new Set(viewState.hiddenColumns),
    [viewState.hiddenColumns]
  )

  const [showReassessConfirm, setShowReassessConfirm] = useState(false)
  const [completionNotice, setCompletionNotice] = useState<string | null>(null)

  const [bulkDelete, setBulkDelete] = useState<{ ids: string[]; aiCount: number } | null>(null)
  const [pendingClear, setPendingClear] = useState<(() => void) | null>(null)

  // Store
  const categoryState = useExtractionStore((s) => s.categories.compliance_matrix)
  const updateItemStatus = useExtractionStore((s) => s.updateItemStatus)
  const updateItemOwner = useExtractionStore((s) => s.updateItemOwner)
  const addManualItem = useExtractionStore((s) => s.addManualItem)
  const deleteItem = useExtractionStore((s) => s.deleteItem)
  const bulkPatchItems = useExtractionStore((s) => s.bulkPatchItems)
  const bulkDeleteItems = useExtractionStore((s) => s.bulkDeleteItems)

  // Compliance loop
  const updateItemPosition = useExtractionStore((s) => s.updateItemPosition)
  const confirmPositions = useExtractionStore((s) => s.confirmPositions)
  const startAssessment = useExtractionStore((s) => s.startAssessment)
  const stopAssessmentPolling = useExtractionStore((s) => s.stopAssessmentPolling)
  const resumeAssessmentPolling = useExtractionStore((s) => s.resumeAssessmentPolling)
  const loadStaleness = useExtractionStore((s) => s.loadStaleness)
  const assessmentRun = useExtractionStore((s) => s.assessmentRun)
  const staleness = useExtractionStore((s) => s.staleness)

  // Coverage (Specs/compliance-checklist-spec.md): derived requirement→section status
  const loadCoverage = useCoverageStore((s) => s.load)
  const coverage = useCoverageStore((s) => s.coverage)
  const coverageError = useCoverageStore((s) => s.error)
  // Section flags (PR 5): bid-wide, so CoverageCell can amber-dot affected chips.
  const loadFlagsForBid = useSectionFlagsStore((s) => s.loadFlagsForBid)

  // Requirement threads (PR 3): one bid-wide count fetch feeds every row badge.
  const countsByItemId = useCommentsStore((s) => s.countsByItemId)
  const loadRequirementCounts = useCommentsStore((s) => s.loadRequirementCounts)

  const items = categoryState.items as ExtractedComplianceItem[]

  /**
   * The drawer is driven off the id, not off a captured row: the store hands
   * back a fresh array on every patch, so holding the object would freeze the
   * drawer on a stale copy of the requirement the user is editing.
   */
  const activeItem = useMemo(
    () => items.find((i) => i.id === activeItemId) ?? null,
    [items, activeItemId]
  )

  // A requirement can leave under the drawer — deleted from the row menu, or
  // dropped by a re-extract. Without this the drawer would sit on nothing.
  useEffect(() => {
    if (activeItemId != null && activeItem == null) setActiveItemId(null)
  }, [activeItemId, activeItem])

  const handleDrawerPositionChange = useCallback(
    (itemId: string, next: PositionValue) => {
      void updateItemPosition(bidId, itemId, next)
    },
    [bidId, updateItemPosition]
  )

  const handleDrawerOwnerChange = useCallback(
    (itemId: string, userId: string | null) => {
      void updateItemOwner(bidId, 'compliance_matrix', itemId, userId)
    },
    [bidId, updateItemOwner]
  )

  const handleDrawerExpectationChange = useCallback(
    (itemId: string, next: ResponseExpectation) => {
      void bulkPatchItems(bidId, 'compliance_matrix', [itemId], {
        responseExpectation: next,
        responseExpectationSource: 'manual',
      }).then(() => loadCoverage(bidId, { force: true }))
    },
    [bidId, bulkPatchItems, loadCoverage]
  )

  const handleDrawerStatusChange = useCallback(
    (itemId: string, status: string) => {
      void updateItemStatus(bidId, 'compliance_matrix', itemId, status)
    },
    [bidId, updateItemStatus]
  )

  const handleDrawerStatusHandBack = useCallback(
    (itemId: string) => {
      void bulkPatchItems(bidId, 'compliance_matrix', [itemId], { statusSource: 'derived' })
    },
    [bidId, bulkPatchItems]
  )

  const closeDrawer = useCallback(() => setActiveItemId(null), [])

  useEffect(() => {
    void loadCoverage(bidId)
    void loadFlagsForBid(bidId)
    void loadStaleness(bidId)
  }, [bidId, loadCoverage, loadFlagsForBid, loadStaleness])

  /**
   * Thread counts, once the matrix actually has rows. Gated on `populated` for
   * the same reason the `?item=` link is: firing it against a still-loading
   * category spends a bid-wide query on a table with nothing to badge.
   */
  const isPopulated = categoryState.state === 'populated'
  useEffect(() => {
    if (!isPopulated) return
    void loadRequirementCounts(bidId)
  }, [bidId, isPopulated, loadRequirementCounts])

  /**
   * And again when the drawer closes — every close path, which is why this is a
   * transition effect rather than a line in `closeDrawer`: clicking the active
   * row again, Escape, and the row disappearing all close it too. Mutations the
   * user made themselves already nudged the store; this catches the comment
   * someone ELSE left while the drawer was open. One query, never on open.
   */
  const drawerWasOpenRef = useRef(false)
  useEffect(() => {
    if (activeItemId !== null) {
      drawerWasOpenRef.current = true
      return
    }
    if (!drawerWasOpenRef.current) return
    drawerWasOpenRef.current = false
    void loadRequirementCounts(bidId)
  }, [activeItemId, bidId, loadRequirementCounts])

  /**
   * Own the assessment poll for as long as this view is mounted.
   *
   * Cleanup: the poll is a module-level interval — without stopping it, it
   * outlives the tab view, and (because the store's re-entrancy guard is
   * bid-agnostic) a stuck `running` status would silently swallow every later
   * trigger.
   *
   * Mount: that same cleanup drops the status to `idle` while the BACKEND run
   * carries on, so switching sub-tabs mid-assessment and coming back showed a
   * matrix with no progress, no refresh when it finished and no completion
   * notice. Resuming re-adopts a run that is still in flight; it is a no-op
   * when there is none.
   */
  useEffect(() => {
    void resumeAssessmentPolling(bidId)
    return stopAssessmentPolling
  }, [bidId, resumeAssessmentPolling, stopAssessmentPolling])

  // `?position=/?criticality=/?source=` are seeded by `useTableFilters` via
  // `complianceUrlSeed`. Post-award is the one part it cannot carry: the seed is
  // a pure function of the params, and post-award visibility is not a dropdown
  // group. An at-risk link means the same view as the at-risk chip, whose count
  // excludes post-award obligations — so the link has to collapse them too, or
  // the link lands on a table wider than the count that sent the user there.
  useEffect(() => {
    if (searchParams.get('position') === AT_RISK_PARAM) setShowPostAward(false)
  }, [searchParams])

  // Report a finished run once. Deliberately neutral wording: when the backend
  // de-dupes a subset trigger into an already-running run, the row count is
  // that run's, not the user's selection. A run nobody clicked for that changed
  // nothing is noise, so it stays quiet; a click always gets an answer.
  const reportedRunRef = useRef<string | null>(null)
  useEffect(() => {
    if (assessmentRun.status !== 'completed' || assessmentRun.bidId !== bidId) return
    if (reportedRunRef.current === assessmentRun.runId) return
    reportedRunRef.current = assessmentRun.runId
    const count = assessmentRun.itemCount ?? 0
    // "Updated 0 positions" after a click read as "nothing happened".
    if (count >= 1) {
      setCompletionNotice(`Assessment updated ${count} position${count === 1 ? '' : 's'}`)
    } else if (assessmentRun.triggeredByUser) {
      setCompletionNotice('Positions already up to date')
    }
    void loadStaleness(bidId)
  }, [
    assessmentRun.status,
    assessmentRun.runId,
    assessmentRun.bidId,
    assessmentRun.itemCount,
    assessmentRun.triggeredByUser,
    bidId,
    loadStaleness,
  ])

  // Fetch team members
  useEffect(() => {
    void api
      .get<{ members: TeamMember[] }>(`/bids/${bidId}/members`)
      .then((data) => setTeamMembers(data.members))
      .catch(() => {
        /* non-critical */
      })
  }, [bidId])

  const coverageByItemId = useMemo(
    () => new Map((coverage?.compliance ?? []).map((row) => [row.requirementId, row])),
    [coverage]
  )

  const expectationOf = useCallback(
    (item: ExtractedComplianceItem): ResponseExpectation => item.responseExpectation ?? 'narrative',
    []
  )

  /** The one coverage lookup every derived-Progress read goes through. */
  const coverageStatusOf = useCallback(
    (item: ExtractedComplianceItem): CoverageStatus | undefined =>
      item.id ? coverageByItemId.get(item.id)?.status : undefined,
    [coverageByItemId]
  )

  // Filters — owner options depend on both the rows and the team members, and
  // the Progress group additionally on coverage.
  const filterGroups = useMemo(
    () => buildComplianceFilterGroups(items, teamMembers, coverageStatusOf),
    [items, teamMembers, coverageStatusOf]
  )

  const filters = useTableFilters<ExtractedComplianceItem>({
    bidId,
    tableKey: 'compliance_matrix',
    groups: filterGroups,
    urlSeed: complianceUrlSeed,
    consumeParams: COMPLIANCE_FILTER_PARAMS,
  })

  // Hoisted because the hook returns a fresh object every render: depending on
  // `filters` itself would re-run these on every render, while these two members
  // are stable (`clearAll`) or memoised on the values (`matches`).
  const { matches: filtersMatch, clearAll: clearAllFilters } = filters

  const postAwardCount = useMemo(
    () => items.filter((item) => expectationOf(item) === 'post_award').length,
    [items, expectationOf]
  )

  /**
   * Everything that can keep a row off this table. The dropdown groups are only
   * part of it — post-award collapse and the coverage strip narrow the view too,
   * and both are outside `filters`. Kept as one predicate so "is my new row
   * visible?" cannot drift from what the table actually renders.
   */
  const isRowVisible = useCallback(
    (item: ExtractedComplianceItem) => {
      if (!filtersMatch(item)) return false
      if (!showPostAward && expectationOf(item) === 'post_award') return false
      if (coverageFilter) {
        return (
          expectationOf(item) === 'narrative' &&
          item.id !== undefined &&
          coverageByItemId.get(item.id)?.status === coverageFilter
        )
      }
      return true
    },
    [filtersMatch, showPostAward, coverageFilter, coverageByItemId, expectationOf]
  )

  // Filtered data — grouped by response expectation (narrative first, then
  // evidence/form; post-award collapsed unless toggled).
  const filteredItems = useMemo(() => {
    return [...items.filter(isRowVisible)].sort(
      (a, b) =>
        EXPECTATION_ORDER.indexOf(expectationOf(a)) - EXPECTATION_ORDER.indexOf(expectationOf(b))
    )
  }, [items, isRowVisible, expectationOf])

  // Position counts are computed over the UNFILTERED, submission-relevant list:
  // a stat that moves when you filter by it is a bug, and post-award
  // obligations are not part of what gets submitted.
  const activeItems = useMemo(() => submissionRelevant(items), [items])

  /**
   * Stats
   *
   * The three progress counts read EFFECTIVE progress, not the stored status:
   * a narrative row whose progress is derived from response coverage is "done"
   * when its sections are addressed, whatever the stored column says. They are
   * counted over `activeItems` for the same reason the position chips are —
   * post-award obligations have no Progress at all, so a stored `completed` on
   * one must not reach the "done" chip.
   */
  const stats = useMemo(() => {
    const total = items.length
    let done = 0
    let inProgress = 0
    let notStarted = 0
    for (const item of activeItems) {
      const { bucket } = effectiveProgress(item, coverageStatusOf(item))
      if (bucket === 'done') done++
      else if (bucket === 'in_progress' || bucket === 'in_review') inProgress++
      else if (bucket === 'not_started') notStarted++
      // `no_action` is deliberately in none of the three.
    }
    const narrative = items.filter((i) => expectationOf(i) === 'narrative').length
    return { total, done, inProgress, notStarted, narrative }
  }, [items, activeItems, coverageStatusOf, expectationOf])

  // The base every "of N" in this strip compares against — the leading total and
  // the coverage lead both. Collapsed post-award rows are out of universe, not
  // "filtered out": at rest this base is exactly `submissionRelevant(items)`,
  // which is what the position chips denominate over, so counting them here
  // would have one strip stating two different sizes for the same register.
  // Expanding post-award widens it on purpose, by a control sitting in the same
  // strip. Derived through the same `expectationOf` as the `filteredItems`
  // exclusion, so the base and the exclusion cannot drift.
  const visibleBase = stats.total - (showPostAward ? 0 : postAwardCount)

  const positionStats = useMemo(() => {
    let partial = 0
    let needsReview = 0
    let needsConfirmation = 0
    let comply = 0
    let notAssessed = 0
    let assessed = 0
    let reassessable = 0
    for (const item of activeItems) {
      const state = displayStateOf(item)
      if (state === 'partial') partial++
      if (state === 'needs_review') needsReview++
      if (state === 'needs_confirmation') needsConfirmation++
      if (state === 'comply') comply++
      if (state === 'not_assessed') notAssessed++
      if (item.positionAssessedAt != null) assessed++
      if ((item.positionSource ?? 'ai') !== 'manual') reassessable++
    }
    return {
      mandatoryAtRisk: countMandatoryRisk(items).atRisk,
      partial,
      needsReview,
      needsConfirmation,
      comply,
      notAssessed,
      assessed,
      reassessable,
      eligible: activeItems.length,
    }
  }, [activeItems, items])

  // The store's re-entrancy guard is bid-agnostic: triggering while ANY run is
  // in flight is a silent no-op, so triggers are disabled on that condition and
  // say why, rather than leaving the user clicking a dead button.
  const anyRunInFlight = assessmentRun.status === 'running'
  const isAssessingThisBid = anyRunInFlight && assessmentRun.bidId === bidId
  const runCompletedHere =
    assessmentRun.status === 'completed' && assessmentRun.bidId === bidId
  const runFailedHere = assessmentRun.status === 'failed' && assessmentRun.bidId === bidId
  /**
   * "Some rows were assessed, some never were" — read off the rows, NOT off the
   * run. `createEmptyAssessmentRun()` puts `assessmentRun` back to `idle` on
   * every mount and nothing rehydrates it, so gating this on a completed run
   * loses the "50 of 200" fact the moment the user reloads — which is exactly
   * when they come back to act on it. `assessed > 0` keeps a never-assessed bid
   * on the first-run CTA instead of "0 of N assessed".
   */
  const hasUnassessedRemainder =
    positionStats.assessed > 0 && positionStats.notAssessed > 0
  // >70% of the rows the library was SEARCHED for abstained — usually a thin
  // library, not a bad tender. Rows never searched by design ("Needs your
  // confirmation": pricing, declarations) say nothing about the library, so
  // they are out of both sides of the ratio; counting them tripped this notice
  // on most real tenders, where they are over half the matrix.
  const searchedAssessed = positionStats.assessed - positionStats.needsConfirmation
  const thinLibrary =
    searchedAssessed > 0 && positionStats.needsReview / searchedAssessed > 0.7
  // Null means "no signal" — not stale, OR a viewer whose staleness probe was
  // refused. Indistinguishable, so it never produces a prompt.
  const isStale =
    staleness !== null &&
    staleness.latestLibraryChangeAt !== null &&
    staleness.latestAssessedAt !== null &&
    staleness.latestLibraryChangeAt > staleness.latestAssessedAt

  const clearNotice = useCallback(() => setCompletionNotice(null), [])

  const applyPositionChip = useCallback(
    (value: PositionParam) => {
      clearNotice()
      filters.toggle('position', value)
    },
    [clearNotice, filters]
  )

  /**
   * EXACT, not a subset. The chip claims to describe the whole view, so it may
   * only light when the two groups hold precisely what it sets — nothing more.
   *
   * A subset test lit the chip over any view CONTAINING the at-risk pair, so
   * ticking Comply alongside them left a chip reading "2 mandatory at risk"
   * above a table showing complying rows. It also armed the chip's own toggle
   * to clear both groups outright, which discarded the extra ticks the user had
   * made by hand. Exactness removes both: if the chip is lit, the only thing in
   * those groups is what the chip put there.
   */
  const positionSelection = filters.values['position']
  const criticalitySelection = filters.values['criticality']
  const atRiskChipActive =
    positionSelection !== undefined &&
    criticalitySelection !== undefined &&
    positionSelection.size === MANDATORY_AT_RISK_STATES.length &&
    MANDATORY_AT_RISK_STATES.every((v) => positionSelection.has(v)) &&
    criticalitySelection.size === 1 &&
    criticalitySelection.has('mandatory')

  // What the post-award toggle was set to when the chip was last lit, and
  // whether the user has moved it themselves since. See the transition below.
  const postAwardBeforeChipRef = useRef(false)
  const postAwardTouchedRef = useRef(false)

  const togglePostAward = useCallback(() => {
    postAwardTouchedRef.current = true
    setShowPostAward((v) => !v)
  }, [])

  /**
   * Arm and restore the post-award collapse off the chip BECOMING lit or dark,
   * not off its button being clicked.
   *
   * Both halves used to live in the click handler, so the chip's own two
   * routes were the only ones that worked. Assembling the same view from the
   * dropdowns lit the chip with post-award still expanded — a chip counting 2
   * over a table showing 3 — and leaving that view by any other route (a chip
   * rail dismissal, `Clear all`, the per-group Clear, a URL re-seed) left
   * post-award collapsed with no lit chip left to explain why.
   *
   * Two traps this shape avoids:
   *
   * - Adjusting state during render rather than in an effect keeps the collapse
   *   in the SAME commit as the filter change. An effect would paint one frame
   *   of post-award rows first.
   * - `prevAtRisk` is seeded from the mount-time value, so a stored or
   *   URL-seeded at-risk selection does not read as a transition and fire a
   *   restore against a `postAwardBeforeChipRef` nobody has set yet.
   */
  const [prevAtRisk, setPrevAtRisk] = useState(atRiskChipActive)
  if (prevAtRisk !== atRiskChipActive) {
    setPrevAtRisk(atRiskChipActive)
    if (atRiskChipActive) {
      postAwardBeforeChipRef.current = showPostAward
      postAwardTouchedRef.current = false
      setShowPostAward(false)
    } else if (!postAwardTouchedRef.current) {
      setShowPostAward(postAwardBeforeChipRef.current)
    }
  }

  /**
   * Undo the narrowing the USER applied — the dropdown groups and the coverage
   * strip. This is the chip rail's "Clear all".
   *
   * Deliberately NOT `showEverything`, and the two must stay apart: post-award
   * collapse is a default nobody chose, with its own always-visible labelled
   * control, so clearing the chips in front of the user must not silently
   * expand a group they never hid.
   */
  const clearAppliedFilters = useCallback(() => {
    clearNotice()
    clearAllFilters()
    setCoverageFilter(null)
  }, [clearNotice, clearAllFilters])

  /**
   * `clearAppliedFilters` PLUS the post-award collapse — the added-row toast's
   * "Clear filters" action, which promises to reveal the one row just created
   * and so has to expand post-award when that row is a post-award obligation.
   * `postAwardTouchedRef` is stamped so a later at-risk chip toggle-off does not
   * re-collapse post-award behind the user; they have just asked to see all of it.
   */
  const showEverything = useCallback(() => {
    clearAppliedFilters()
    setShowPostAward(true)
    postAwardTouchedRef.current = true
  }, [clearAppliedFilters])

  /**
   * `?item=<complianceItemId>` — the requirement-level deep link (spec A3).
   *
   * Unlike the filter params this names ONE row, so it cannot rely on the
   * view it lands in: whatever the user last left stored, plus the post-award
   * collapse nobody chose, can all be hiding it. `showEverything` is the only
   * widening that clears both, and it runs only when the row is actually
   * hidden — an arriving link must not wipe a view it did not need to.
   *
   * Three things this shape is load-bearing about:
   *
   * - It waits for `populated`. Spending the link against a still-loading
   *   category would toast "no longer in this matrix" about a requirement that
   *   simply has not arrived, and spend the link on the way past.
   * - The one-shot ref is what stops the drawer re-opening: the effect re-runs
   *   on every `items` identity change (the store hands back a fresh array on
   *   every patch), and without it closing the drawer would immediately undo
   *   itself.
   * - It is declared AFTER `useTableFilters`, deliberately. Both consume params
   *   in an effect, both compute from the same render's `searchParams`, and the
   *   later navigation wins. Declared first, this one's deletion of `item`
   *   would be overwritten and the link would stick in the address bar forever.
   */
  const itemParamConsumedRef = useRef(false)
  useEffect(() => {
    if (itemParamConsumedRef.current) return
    const itemParam = searchParams.get('item')
    if (!itemParam) return
    if (categoryState.state !== 'populated') return
    itemParamConsumedRef.current = true

    const target = items.find((i) => i.id === itemParam)
    if (!target) {
      toast.info('That requirement is no longer in this matrix')
    } else {
      if (!isRowVisible(target)) showEverything()
      setActiveItemId(itemParam)
      // One frame later the widened table has committed, so the row exists.
      requestAnimationFrame(() => {
        document
          .querySelector(`[data-row-id="${CSS.escape(itemParam)}"]`)
          ?.scrollIntoView({ block: 'center' })
      })
    }

    // Spent on both paths — a link left in the URL re-toasts, and survives a
    // reload to re-open a drawer the user has since closed. Only `item` goes:
    // `view=` is the Analysis sub-tab's own param.
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        next.delete('item')
        return next
      },
      { replace: true }
    )
  }, [searchParams, categoryState.state, items, isRowVisible, showEverything, setSearchParams])

  /**
   * The coverage strip narrows the table from outside `useTableFilters`, so it
   * gets no chip of its own and `Clear all` cannot see it. Railing it here is
   * what keeps it clearable when its own button is gone — the strip hides a
   * status once its count reaches zero, which is exactly what mapping the last
   * unmapped requirement does while the filter is still on.
   */
  const coverageChips = useMemo<ExtraFilterChip[]>(() => {
    if (!coverageFilter) return []
    return [
      {
        key: `coverage:${coverageFilter}`,
        groupLabel: 'Coverage',
        label: coverageStatusConfig[coverageFilter].label,
        onRemove: () => {
          clearNotice()
          setCoverageFilter(null)
        },
      },
    ]
  }, [coverageFilter, clearNotice])

  // Two nodes from one call: the dropdowns go inline in the toolbar, the chip
  // rail goes on its own full-width row beneath it. See `useTableFilterBar`.
  const filterBar = useTableFilterBar({
    groups: filterGroups,
    values: filters.values,
    activeChips: filters.activeChips,
    extraChips: coverageChips,
    onToggle: (key, value) => {
      clearNotice()
      filters.toggle(key, value)
    },
    onClear: (key) => {
      clearNotice()
      filters.clear(key)
    },
    // The filter groups AND the coverage strip — but not the post-award
    // collapse, which the user never applied. `showEverything` (the toast's
    // action) is the one that also expands it.
    onClearAll: clearAppliedFilters,
  })

  /**
   * The chip means "mandatory AND at risk", so it sets both groups — unlike
   * `?position=at_risk`, which means at risk at any criticality.
   *
   * Filters only. The post-award collapse that has to accompany this view — the
   * chip's count runs through `submissionRelevant()`, so it never includes
   * post-award obligations — is handled by the transition above, which sees
   * this view arrive from the dropdowns and the chip rail just as well as from
   * here. Clearing both groups is safe precisely because `atRiskChipActive` is
   * exact: when it is lit, they hold nothing but what the chip set.
   */
  const toggleAtRiskChip = useCallback(() => {
    clearNotice()
    if (atRiskChipActive) filters.setMany({ position: [], criticality: [] })
    else filters.setMany({ position: [...MANDATORY_AT_RISK_STATES], criticality: ['mandatory'] })
  }, [atRiskChipActive, clearNotice, filters])

  const handleAssessAll = useCallback(() => {
    clearNotice()
    void startAssessment(bidId)
  }, [bidId, startAssessment, clearNotice])

  // Columns
  const columns = useMemo<ColumnDef<ExtractedComplianceItem, unknown>[]>(
    () => [
      {
        // Stable per-bid number (REQ-n), not the row's position. Sorted
        // numerically on displayNumber; unnumbered rows sit last either way.
        id: 'req',
        accessorFn: (item) => item.displayNumber ?? undefined,
        sortingFn: 'basic',
        sortUndefined: 'last',
        sortDescFirst: false,
        size: 72,
        // The component itself, not an inline arrow: flexRender mounts a
        // function header as a component, so a fresh arrow on every columns
        // rebuild remounted the button mid-click and the sort never fired.
        header: ReqSortHeader,
        cell: ({ row }) => (
          <span className="text-meta whitespace-nowrap tabular-nums">
            {formatReqRef(row.original.displayNumber) ?? '—'}
          </span>
        ),
      },
      {
        accessorKey: 'title',
        header: 'Requirement',
        size: 300,
        cell: ({ row }) => {
          const item = row.original
          // The description line is gone: it was the largest single contributor
          // to row height, and the drawer carries it in full. The `title` attr
          // keeps it one hover away.
          return (
            <div title={item.description || undefined}>
              {item.criticality === 'mandatory' && (
                <Badge
                  variant="destructive"
                  dot={false}
                  title="Mandatory"
                  className="text-[10px] px-1 py-0 mr-1.5 font-medium"
                >
                  M
                </Badge>
              )}
              <span
                className={cn(
                  'text-sm',
                  item.criticality === 'information' && 'text-muted-foreground'
                )}
              >
                {item.title}
              </span>
            </div>
          )
        },
      },
      {
        id: 'position',
        // "Position" is the internal enum's name; "Can we comply?" is the
        // question a bid manager is actually answering in this column.
        header: 'Can we comply?',
        size: 130,
        cell: ({ row }) => (
          <PositionPill
            position={row.original.position ?? 'unassessed'}
            positionSource={row.original.positionSource ?? 'ai'}
            assessedAt={row.original.positionAssessedAt ?? null}
            unassessedReason={row.original.unassessedReason ?? null}
            disabled={readOnly || !row.original.id}
            onChange={(next) => {
              if (row.original.id) {
                void updateItemPosition(bidId, row.original.id, next)
              }
            }}
          />
        ),
      },
      {
        accessorKey: 'category',
        header: 'Category',
        size: 140,
        cell: ({ row }) => {
          const cat = row.original.category
          const colors = COMPLIANCE_CATEGORY_COLORS[cat] ?? 'bg-gray-100 text-gray-800'
          return (
            <Badge
              variant="outline"
              className={cn('text-xs px-1.5 py-0 font-normal', colors)}
            >
              {cat}
            </Badge>
          )
        },
      },
      {
        accessorKey: 'refClause',
        header: 'Ref/Clause',
        size: 100,
        cell: ({ row }) => (
          <span className="text-meta">
            {row.original.refClause ?? '—'}
          </span>
        ),
      },
      {
        id: 'expectation',
        header: 'Response type',
        size: 110,
        cell: ({ row }) => {
          const item = row.original
          return (
            <ExpectationPill
              value={item.responseExpectation ?? 'narrative'}
              readOnly={readOnly || !item.id}
              onChange={(next) => {
                if (item.id) {
                  void bulkPatchItems(bidId, 'compliance_matrix', [item.id], {
                    responseExpectation: next,
                    responseExpectationSource: 'manual',
                  }).then(() => loadCoverage(bidId, { force: true }))
                }
              }}
            />
          )
        },
      },
      {
        id: 'coverage',
        header: 'Where answered',
        size: 130,
        // Status only. Mapping a requirement to sections is a deliberate act
        // that needs to see what it is mapping, so `CoverageCell` — with its
        // section chips, suggestions and "+ Map" picker — lives in the drawer.
        // Inline it was the widest cell on the table and the easiest to
        // misfire in.
        cell: ({ row }) => {
          const item = row.original
          const expectation = item.responseExpectation ?? 'narrative'
          if (expectation !== 'narrative') {
            const { hint } = expectationConfig[expectation]
            return hint ? (
              <span className="text-[11px] text-muted-foreground">{hint}</span>
            ) : null
          }
          const status = item.id ? coverageByItemId.get(item.id)?.status : undefined
          if (!status) return <span className="text-meta">—</span>
          return (
            <Badge
              variant={coverageStatusConfig[status].variant}
              dot
              className="text-xs font-normal whitespace-nowrap"
            >
              {coverageStatusConfig[status].label}
            </Badge>
          )
        },
      },
      {
        id: 'owner',
        header: 'Owner',
        size: 100,
        cell: ({ row }) => (
          <OwnerPicker
            value={row.original.ownerId}
            teamMembers={teamMembers}
            disabled={readOnly ?? false}
            onChange={(userId) => {
              if (row.original.id) {
                void updateItemOwner(
                  bidId,
                  'compliance_matrix',
                  row.original.id,
                  userId
                )
              }
            }}
          />
        ),
      },
      {
        id: 'source',
        header: 'Source',
        size: 70,
        cell: ({ row }) => (
          <CitationLink
            page={row.original.sourcePage}
            documentId={row.original.sourceDocumentId ?? null}
            sourceQuote={row.original.sourceQuote}
            sourceHighlightData={row.original.sourceHighlightData ?? null}
          />
        ),
      },
      {
        id: 'progress',
        header: 'Progress',
        size: 120,
        // No hand-back here: returning a row to derivation is a deliberate,
        // explained act, and the drawer is where the explanation fits.
        cell: ({ row }) => (
          <ProgressCell
            item={row.original}
            coverageStatus={
              row.original.id ? coverageByItemId.get(row.original.id)?.status : undefined
            }
            readOnly={!!readOnly}
            variant="row"
            onStatusChange={(status) => {
              if (row.original.id) {
                void updateItemStatus(
                  bidId,
                  'compliance_matrix',
                  row.original.id,
                  status
                )
              }
            }}
          />
        ),
      },
      {
        id: 'discussion',
        header: 'Discussion',
        size: 60,
        // Deliberately NOT marked `data-no-row-activate`: this cell is a
        // signpost, not a control. Clicking it activates the row like any
        // other, and the drawer is where the thread actually lives.
        cell: ({ row }) => {
          const counts = countsByItemId[row.original.id ?? '']
          const total = counts?.total ?? 0
          const unread =
            !!counts &&
            counts.total > 0 &&
            (counts.watermark === null || counts.lastActivityAt > counts.watermark)
          // An icon-only cell says nothing to a screen reader, and the unread
          // dot is otherwise carried by colour alone.
          const label =
            total === 0
              ? 'No discussion'
              : `${total} comment${total === 1 ? '' : 's'}${unread ? ', unread' : ''}`
          return (
            <span
              aria-label={label}
              title={label}
              className={cn(
                'inline-flex items-center gap-1 text-sm',
                total === 0 ? 'text-subtle' : 'text-muted-foreground'
              )}
            >
              <MessageSquare className="h-4 w-4" />
              {total > 0 && total}
              {unread && (
                <span className="h-1.5 w-1.5 rounded-full bg-[hsl(var(--brand))]" />
              )}
            </span>
          )
        },
      },
      {
        id: 'actions',
        header: '',
        size: 40,
        cell: ({ row }) => {
          const canDelete =
            !readOnly && row.original.extractionSource === 'manual' && !!row.original.id
          // Delete is the only thing left in this menu: the drawer shows the
          // source quote inline, so View/Copy source no longer earn a kebab of
          // their own. A row with nothing to offer renders none (spec A2) —
          // the other four tables keep the full menu.
          return canDelete ? (
            <RowActionsMenu
              source="manual"
              sourceQuote={null}
              onDelete={() => void deleteItem(bidId, 'compliance_matrix', row.original.id!)}
            />
          ) : null
        },
      },
    ],
    [bidId, teamMembers, updateItemStatus, updateItemOwner, deleteItem, bulkPatchItems, updateItemPosition, loadCoverage, readOnly, coverageByItemId, countsByItemId]
  )

  /**
   * Render-level grouping. Every accessor returns the LABEL a user reads, not
   * the stored value, so the group header needs no second lookup and the
   * headings match the pills in the rows beneath them.
   */
  const groupByConfig = useMemo(() => {
    const key = viewState.groupBy
    if (!key) return null

    const renderHeader = (groupKey: string, rows: ExtractedComplianceItem[]) => {
      const atRisk = rows.filter(isAtRisk).length
      return (
        <span className="flex items-baseline gap-2">
          <span className="text-sm font-medium">{groupKey}</span>
          <span className="text-meta">
            {rows.length}
            {atRisk > 0 ? ` · ${atRisk} at risk` : ''}
          </span>
        </span>
      )
    }

    if (key === 'category') {
      return {
        id: key,
        accessor: (item: ExtractedComplianceItem) => item.category,
        groupOrder: [...ALL_CATEGORIES],
        renderHeader,
      }
    }

    if (key === 'owner') {
      const nameOf = new Map(
        teamMembers.map((m) => [m.userId, m.fullName ?? UNKNOWN_OWNER_GROUP] as const)
      )
      // `UNKNOWN_OWNER_GROUP` is excluded here because it is appended below —
      // a member with a null `fullName` folds onto the same bucket as an owner
      // id no member can name, and listing it twice is not cosmetic:
      // `ExtractionTable` orders groups with `groupOrder.filter(...)`, which
      // does not dedupe, so the group rendered twice with every row doubled.
      const named = [...new Set(nameOf.values())]
        .filter((name) => name !== UNKNOWN_OWNER_GROUP)
        .sort((a, b) => a.localeCompare(b))
      return {
        id: key,
        accessor: (item: ExtractedComplianceItem) =>
          item.ownerId ? (nameOf.get(item.ownerId) ?? UNKNOWN_OWNER_GROUP) : UNASSIGNED_GROUP,
        // Unowned rows last: they are the pile to work through, not a person.
        groupOrder: [...named, UNKNOWN_OWNER_GROUP, UNASSIGNED_GROUP],
        renderHeader,
      }
    }

    if (key === 'position') {
      return {
        id: key,
        accessor: (item: ExtractedComplianceItem) => {
          const state = displayStateOf(item)
          return POSITION_FILTER_OPTIONS.find((o) => o.value === state)?.label ?? state
        },
        groupOrder: POSITION_FILTER_OPTIONS.map((o) => o.label),
        renderHeader,
      }
    }

    return {
      id: key,
      accessor: (item: ExtractedComplianceItem) =>
        expectationConfig[item.responseExpectation ?? 'narrative'].label,
      groupOrder: EXPECTATION_ORDER.map((e) => expectationConfig[e].label),
      renderHeader,
    }
  }, [viewState.groupBy, teamMembers])

  const toolbarExtras = (
    <>
      <GroupBySelect options={GROUP_OPTIONS} value={viewState.groupBy} onChange={setGroupBy} />
      <ColumnsPicker
        options={HIDEABLE_COLUMNS}
        hidden={hiddenColumnSet}
        onToggle={toggleColumn}
      />
    </>
  )

  // Export
  const handleExport = useCallback(() => {
    const exportData = filteredItems.map((item) => {
      const { body, action } = splitRationaleAction(item.positionRationale)
      const expectation = expectationOf(item)
      const coverageStatus =
        expectation === 'narrative' && item.id
          ? coverageByItemId.get(item.id)?.status
          : undefined
      const { bucket: progressBucket } = effectiveProgress(item, coverageStatus)
      return {
        REQ: formatReqRef(item.displayNumber) ?? '',
        Requirement: item.title,
        Description: item.description || '',
        Category: item.category,
        Criticality:
          CRITICALITY_OPTIONS.find((o) => o.value === item.criticality)?.label ?? '',
        'Ref/Clause': item.refClause || '',
        // Same label the reviewer sees in the table, including the
        // assessed-but-unresolved split ("Needs review" or "Needs your
        // confirmation", never "Unassessed").
        Position: displayPositionLabel(
          item.position ?? 'unassessed',
          item.positionAssessedAt ?? null,
          item.unassessedReason ?? null
        ),
        Rationale: body,
        // AI-owned rows export the action verbatim — there it is genuinely live.
        'Recommended action': action
          ? item.positionSource === 'manual'
            ? `${MANUAL_ACTION_PREFIX}${action}`
            : action
          : '',
        Evidence: item.evidence || '',
        // Coverage is a narrative-only concept, exactly as in the table.
        'Response Coverage': coverageStatus ? coverageStatusConfig[coverageStatus].label : '',
        // The note text now lives in the requirement thread; a frozen notes
        // string would be stale data on an audit artefact. Counts here;
        // thread-body export is a tracked backlog item.
        Discussion: (() => {
          const c = item.id ? countsByItemId[item.id] : undefined
          if (!c || c.total === 0) return ''
          return `${c.total} comment${c.total === 1 ? '' : 's'} (${c.open} open)`
        })(),
        // The sheet has to say what the screen says: effective Progress, not
        // the stored status a derived row does not own. A post-award row has no
        // Progress at all, and must say so rather than borrow "Not started".
        Progress: progressBucket === null ? 'Post-award' : PROGRESS_LABELS[progressBucket],
        Source: item.sourceDocument
          ? formatSourceWithPage(item.sourceDocument, item.sourcePage)
          : '',
      }
    })
    // Two narrowings on this table have no filter chip: the coverage strip, and
    // the post-award group, which is collapsed by DEFAULT — so its rows go
    // missing from the sheet without the user having chosen anything. The note
    // describes the file, so both belong in it.
    const extras: string[] = []
    if (coverageFilter) {
      extras.push(`Coverage: ${coverageStatusConfig[coverageFilter].label}`)
    }
    if (!showPostAward && postAwardCount > 0) {
      extras.push(`${postAwardCount} post-award obligation(s) hidden`)
    }
    const scopeNote = exportScopeNote({
      chips: filters.activeChips,
      shown: filteredItems.length,
      total: items.length,
      extras,
      // REQ numbers are stable per bid — the sheet keeps the matrix's own.
      numberingNote: 'REQ numbers match the full matrix.',
    })

    // Row 1 always carries the forwarding safeguard — this is a single INTERNAL
    // working document, and a spreadsheet that gets emailed on has to say so on
    // its own face — with the scope warning ahead of it when there is one.
    const worksheet = buildExportSheet(
      exportData,
      scopeNote ? `${scopeNote} ${EXPORT_INTERNAL_NOTE}` : EXPORT_INTERNAL_NOTE
    )
    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Compliance Matrix')
    XLSX.writeFile(
      workbook,
      `compliance-matrix-${new Date().toISOString().split('T')[0]}.xlsx`
    )
  }, [
    filteredItems,
    expectationOf,
    coverageByItemId,
    countsByItemId,
    filters.activeChips,
    items.length,
    coverageFilter,
    showPostAward,
    postAwardCount,
  ])

  // Add manual item
  const handleAddManualItem = useCallback(
    async (values: Record<string, string | boolean>) => {
      await addItemWithFeedback(
        'Requirement',
        () =>
          addManualItem(bidId, 'compliance_matrix', {
            title: typeof values['title'] === 'string' ? values['title'] : '',
            description: typeof values['description'] === 'string' ? values['description'] : '',
            category: (typeof values['category'] === 'string' ? values['category'] : 'Other') as ComplianceCategory,
            refClause: typeof values['refClause'] === 'string' && values['refClause'] ? values['refClause'] : null,
            evidence: typeof values['evidence'] === 'string' && values['evidence'] ? values['evidence'] : null,
            criticality: (typeof values['criticality'] === 'string' && values['criticality']
              ? values['criticality']
              : 'desirable') as CriticalityValue,
            note: typeof values['note'] === 'string' && values['note'] ? values['note'] : null,
            status: (typeof values['status'] === 'string' && values['status'] ? values['status'] : 'pending'),
            ownerId: typeof values['ownerId'] === 'string' && values['ownerId'] ? values['ownerId'] : null,
            sourceQuote: '',
            sourceDocument: '',
            sourcePage: 0,
            extractionSource: 'manual',
          }),
        {
          isVisible: (created) => isRowVisible(created as ExtractedComplianceItem),
          onShowItem: showEverything,
        }
      )
    },
    [bidId, addManualItem, isRowVisible, showEverything]
  )

  const modalFields: ModalFieldConfig[] = useMemo(() => [
    { key: 'title', label: 'Requirement', type: 'text', required: true, fullWidth: true },
    { key: 'description', label: 'Description', type: 'textarea' },
    { key: 'category', label: 'Category', type: 'select', required: true, options: ALL_CATEGORIES, defaultValue: 'Other' },
    { key: 'refClause', label: 'Ref/Clause', type: 'text', placeholder: 'e.g. Schedule 3, Clause 4.2' },
    { key: 'evidence', label: 'Evidence', type: 'text' },
    {
      key: 'criticality',
      label: 'Criticality',
      type: 'select',
      options: CRITICALITY_OPTIONS.map((o) => ({ value: o.value, label: o.label })),
      defaultValue: 'desirable',
    },
    { key: 'ownerId', label: 'Owner', type: 'owner' },
    { key: 'status', label: 'Status', type: 'select', options: COMPLIANCE_STATUS_OPTIONS.map((o) => ({ value: o.value, label: o.label })), defaultValue: 'pending' },
    { key: 'note', label: 'Note', type: 'textarea' },
  ], [])

  // Global filter
  const globalFilterFn = useCallback(
    (row: ExtractedComplianceItem, filterValue: string) => {
      const search = filterValue.toLowerCase()
      // "REQ-12", "req 12" or a bare "12" also finds REQ-12. The number must
      // match exactly — "12" never pulls in REQ-112 — and the text fields below
      // still match as they always did.
      const trimmed = search.trim()
      const reqNumber = /^\d+$/.test(trimmed) ? Number(trimmed) : parseReqRef(trimmed)
      return (
        (reqNumber !== null && row.displayNumber === reqNumber) ||
        row.title.toLowerCase().includes(search) ||
        row.description.toLowerCase().includes(search) ||
        row.category.toLowerCase().includes(search) ||
        (row.refClause?.toLowerCase().includes(search) ?? false)
      )
    },
    []
  )

  const openReassessConfirm = () => {
    clearNotice()
    setShowReassessConfirm(true)
  }

  const positionChips: Array<{ value: PositionParam; label: string; count: number }> = [
    { value: 'partial', label: 'partial', count: positionStats.partial },
    { value: 'needs_review', label: 'needs review', count: positionStats.needsReview },
    {
      value: 'needs_confirmation',
      // The one chip that reads as a sentence about people, so it agrees in
      // number: "1 needs your confirmation", "12 need your confirmation".
      label:
        positionStats.needsConfirmation === 1
          ? 'needs your confirmation'
          : 'need your confirmation',
      count: positionStats.needsConfirmation,
    },
    { value: 'comply', label: 'comply', count: positionStats.comply },
  ]

  const inFlightTitle = anyRunInFlight ? 'An assessment is already running' : undefined

  // One state at a time, in priority order. Everything below the notice is a
  // write action, so viewers see nothing here.
  const assessmentSlot = (() => {
    if (isAssessingThisBid) {
      return (
        <span className="flex items-center gap-1.5">
          <Loader2 className="h-3 w-3 animate-spin" />
          Assessing compliance…
        </span>
      )
    }
    // A run that ended without doing the work outranks everything below: it is
    // the only branch that explains why nothing changed. Silence here is how a
    // disabled kill switch used to read as a 15-minute spinner.
    if (runFailedHere) {
      return (
        <span className="text-foreground">
          {assessmentRun.errorMessage ?? 'Assessment could not be completed'}
        </span>
      )
    }
    // Outranks the completion notice deliberately: a capped run that reports
    // only "Assessment updated 50 positions" reads as "the matrix is done" when
    // 150 rows were never looked at.
    if (hasUnassessedRemainder) {
      return (
        <span className="flex items-center gap-1">
          {positionStats.assessed} of {positionStats.eligible} assessed —
          {readOnly ? (
            <span>reassessment needed to continue</span>
          ) : (
            <Button
              variant="ai-ghost"
              size="sm"
              className="h-7 px-2 text-xs"
              disabled={anyRunInFlight}
              title={inFlightTitle}
              onClick={openReassessConfirm}
            >
              Reassess to continue
            </Button>
          )}
        </span>
      )
    }
    if (completionNotice) {
      return <span className="text-foreground">{completionNotice}</span>
    }
    if (readOnly || positionStats.eligible === 0) return null
    if (positionStats.assessed === 0) {
      return (
        <Button
          variant="ai-ghost"
          size="sm"
          className="h-7 gap-1.5 px-2 text-xs"
          disabled={anyRunInFlight}
          title={inFlightTitle}
          onClick={handleAssessAll}
        >
          <GradientSparkleIcon className="h-3.5 w-3.5" />
          Assess compliance
        </Button>
      )
    }
    if (runCompletedHere && thinLibrary) {
      return (
        <span>
          Little library evidence found —{' '}
          <Link
            to="/app/library"
            className="underline underline-offset-2 hover:text-foreground"
          >
            add company documents
          </Link>{' '}
          to improve assessment
        </span>
      )
    }
    if (isStale) {
      return (
        <span className="flex items-center gap-1">
          Library updated since last assessment —
          <Button
            variant="ai-ghost"
            size="sm"
            className="h-7 px-2 text-xs"
            disabled={anyRunInFlight}
            title={inFlightTitle}
            onClick={openReassessConfirm}
          >
            Reassess
          </Button>
        </span>
      )
    }
    return (
      <Button
        variant="ghost"
        size="icon"
        className="h-7 w-7"
        aria-label="Reassess all positions"
        title="Reassess"
        disabled={anyRunInFlight}
        onClick={openReassessConfirm}
      >
        <Sparkles className="h-3.5 w-3.5 text-[hsl(var(--ai-accent))]" />
      </Button>
    )
  })()

  // Height-bounds the docked aside so its body scrolls internally rather than
  // running the page. Below lg the drawer portals into a Sheet and this
  // wrapper renders empty.
  // `shrink min-w-[340px]`: when the docked reader also claims width the drawer
  // compresses toward 340px instead of overflowing the row and sliding under
  // the reader.
  // 10rem, not 7rem: ~150px of page chrome sits above the table before the page
  // scrolls, so a 7rem cap hangs the drawer's last section below the fold at
  // scroll 0 with no way to reach it.
  const detailPanel = activeItem ? (
    <div className="sticky top-4 flex max-h-[calc(100vh-10rem)] min-w-[340px] shrink self-start">
      <RequirementDrawer
        bidId={bidId}
        item={activeItem}
        onClose={closeDrawer}
        readOnly={readOnly ?? false}
        teamMembers={teamMembers}
        coverageStatus={activeItem?.id ? coverageByItemId.get(activeItem.id)?.status : undefined}
        onPositionChange={handleDrawerPositionChange}
        onOwnerChange={handleDrawerOwnerChange}
        onExpectationChange={handleDrawerExpectationChange}
        onStatusChange={handleDrawerStatusChange}
        onStatusHandBack={handleDrawerStatusHandBack}
      />
    </div>
  ) : null

  return (
    <>
    <ExtractionTable<ExtractedComplianceItem>
      bidId={bidId}
      category="compliance_matrix"
      title="Compliance Matrix"
      description="Extracted by AI from your tender documents — verify against source and add anything missed."
      columns={columns}
      data={filteredItems}
      readOnly={readOnly ?? false}
      onExport={handleExport}
      activeRowId={activeItemId}
      onRowActivate={setActiveItemId}
      columnVisibility={columnVisibility}
      toolbarExtras={toolbarExtras}
      groupBy={groupByConfig}
      {...(!readOnly ? { onAddManualItem: () => setShowAddRow(true) } : {})}
      enableSelection={!readOnly}
      renderBulkActions={(rows, clear) => {
        const ids = rows.map((r) => r.id!).filter(Boolean)
        // A "Needs your confirmation" row has no AI verdict to attest to — the
        // person answers it by choosing a position — so Confirm skips it, and
        // is disabled when nothing else is selected. It keeps its label, which
        // is what shows it was not confirmed.
        const confirmableIds = rows
          .filter((r) => displayStateOf(r) !== 'needs_confirmation')
          .map((r) => r.id!)
          .filter(Boolean)
        const aiCount = rows.filter((r) => r.extractionSource !== 'manual').length
        return (
          <BulkActionsBar count={rows.length} onClear={clear}>
            <BulkOwnerPicker
              teamMembers={teamMembers}
              onSelect={(userId) => {
                void (async () => {
                  await bulkPatchItems(bidId, 'compliance_matrix', ids, { ownerId: userId })
                  clear()
                })()
              }}
            />
            <BulkValuePicker
              label="Status"
              options={COMPLIANCE_STATUS_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
              onSelect={(status) => {
                void (async () => {
                  await bulkPatchItems(bidId, 'compliance_matrix', ids, { status })
                  clear()
                })()
              }}
            />
            <BulkValuePicker
              label="Position"
              options={POSITION_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
              onSelect={(position) => {
                void (async () => {
                  await bulkPatchItems(bidId, 'compliance_matrix', ids, { position })
                  clear()
                })()
              }}
            />
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs text-background hover:bg-background/15 hover:text-background gap-1"
              disabled={confirmableIds.length === 0}
              onClick={() => {
                void (async () => {
                  await confirmPositions(bidId, confirmableIds)
                  clear()
                })()
              }}
            >
              <Check className="h-3.5 w-3.5" />
              Confirm positions
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs text-background hover:bg-background/15 hover:text-background gap-1"
              disabled={anyRunInFlight}
              title={anyRunInFlight ? 'An assessment is already running' : undefined}
              onClick={() => {
                void (async () => {
                  clearNotice()
                  await startAssessment(bidId, ids)
                  clear()
                })()
              }}
            >
              <Sparkles className="h-3.5 w-3.5" />
              Reassess
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs text-background hover:bg-destructive hover:text-destructive-foreground gap-1"
              onClick={() => {
                setBulkDelete({ ids, aiCount })
                setPendingClear(() => clear)
              }}
            >
              <Trash2 className="h-3.5 w-3.5" />
              Delete
            </Button>
          </BulkActionsBar>
        )
      }}
      globalFilterFn={globalFilterFn}
      filterContent={filterBar.controls}
      filterChips={filterBar.chips}
      statsBar={(visibleCount) => (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <div className="flex items-center gap-2 text-meta">
            {/* `visibleCount`, not `filteredItems.length`: the search box lives
                inside ExtractionTable, so only it knows what is on screen. The
                per-status counts below deliberately keep counting the whole
                set — a stat that moves when you filter by it is a bug. */}
            <span>
              {visibleCount === visibleBase
                ? `${visibleBase} items`
                : `${visibleCount} of ${visibleBase} items`}
            </span>
            <span>·</span>
            <span className="text-green-600">{stats.done} done</span>
            <span>·</span>
            <span className="text-amber-500">{stats.inProgress} in progress</span>
            <span>·</span>
            <span>{stats.notStarted} not started</span>
          </div>
          <div className="flex flex-wrap items-center gap-1.5 text-meta">
            {positionStats.mandatoryAtRisk > 0 && (
              <button
                type="button"
                onClick={toggleAtRiskChip}
                // These chips are toggles whose lit state is otherwise carried
                // only by a background wash and a font weight — invisible to a
                // screen reader, and to any honest test.
                aria-pressed={atRiskChipActive}
                className={cn(
                  'rounded-md px-1.5 py-0.5 text-destructive transition-colors hover:bg-accent',
                  atRiskChipActive && 'bg-accent font-medium'
                )}
              >
                {positionStats.mandatoryAtRisk} mandatory at risk
              </button>
            )}
            {positionChips
              .filter((chip) => chip.count > 0)
              .map((chip, index) => (
                <span key={chip.value} className="flex items-center gap-1.5">
                  {index > 0 && <span aria-hidden="true">·</span>}
                  <button
                    type="button"
                    onClick={() => applyPositionChip(chip.value)}
                    aria-pressed={filters.values['position']?.has(chip.value) === true}
                    className={cn(
                      'rounded-md px-1.5 py-0.5 transition-colors hover:bg-accent hover:text-foreground',
                      filters.values['position']?.has(chip.value) === true &&
                        'bg-accent font-medium text-foreground'
                    )}
                  >
                    {chip.count} {chip.label}
                  </button>
                </span>
              ))}
            {assessmentSlot}
          </div>
          {/* `|| coverageFilter` for the same reason the strip itself keeps a
              zero-count button: an emptied register must not take the only
              control for a live filter down with it. */}
          {coverage && (coverage.complianceSummary.total > 0 || coverageFilter) && (
            <CoverageSummaryStrip
              bidId={bidId}
              summary={coverage.complianceSummary}
              lead={`${stats.narrative} of ${visibleBase} require response coverage:`}
              activeFilter={coverageFilter}
              onFilter={(status) => {
                clearNotice()
                setCoverageFilter(status)
              }}
              readOnly={readOnly ?? false}
            />
          )}
          {/* A failed coverage fetch used to render as nothing at all, which
              reads as "every requirement is covered". */}
          {!coverage && coverageError && (
            <span className="text-meta">Coverage unavailable</span>
          )}
          {postAwardCount > 0 && (
            <button
              type="button"
              onClick={togglePostAward}
              className="flex items-center gap-1 text-meta hover:text-foreground"
            >
              <ChevronRight className={cn('h-3 w-3 transition-transform', showPostAward && 'rotate-90')} />
              {postAwardCount} post-award {postAwardCount === 1 ? 'obligation' : 'obligations'} — not
              required for submission
            </button>
          )}
        </div>
      )}
      detailPanel={detailPanel}
    />
    <AddItemModal
      open={showAddRow}
      onOpenChange={setShowAddRow}
      title="Add compliance item"
      description="Add a manual compliance requirement to track alongside AI-extracted items."
      fields={modalFields}
      teamMembers={teamMembers}
      onSubmit={handleAddManualItem}
    />
    <BulkConfirmDelete
      open={bulkDelete !== null}
      onOpenChange={(open) => {
        if (!open) {
          setBulkDelete(null)
          setPendingClear(null)
        }
      }}
      count={bulkDelete?.ids.length ?? 0}
      aiCount={bulkDelete?.aiCount ?? 0}
      itemNoun="compliance item"
      onConfirm={() => {
        void (async () => {
          if (!bulkDelete) return
          await bulkDeleteItems(bidId, 'compliance_matrix', bulkDelete.ids)
          pendingClear?.()
          setBulkDelete(null)
          setPendingClear(null)
        })()
      }}
    />
    <AlertDialog open={showReassessConfirm} onOpenChange={setShowReassessConfirm}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Reassess compliance positions</AlertDialogTitle>
          <AlertDialogDescription>
            {positionStats.reassessable} AI-assessed{' '}
            {positionStats.reassessable === 1 ? 'position' : 'positions'} will be recalculated.
            Positions you confirmed or set yourself are left as they are.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={() => {
              setShowReassessConfirm(false)
              handleAssessAll()
            }}
          >
            Reassess
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  )
}
